import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { getDisplayName, getTitleOrEmpty, numberWithSpaces } from 'shared/helpers/common';
import { getFieldFromRootOrCharacteristics, getCountryById } from 'shared/helpers/transportInfo';
import {
  SpeedIcon,
  GearboxIcon,
  CalendarIcon,
  EngineIcon,
  ChassisIcon,
  LocationIcon,
  ColorIcon,
  VinIcon,
  CarIcon,
} from 'shared/components/Icons';
import { generateSearchUrl, getDealerLink } from 'shared/helpers/urls';
import { getUserName } from 'shared/helpers/users';
import { cityLocale } from 'shared/modules';
import { cleanArray } from 'shared/helpers/arrays';
import Link from 'shared/components/Link/Link';
import powerUnits from 'shared/constants/json/powerUnits.json';
import mileageUnits from 'shared/constants/json/mileageUnits.json';
import capacityUnits from 'shared/constants/json/capacityUnits.json';
import currencies from 'shared/constants/json/currency.json';
import { getDefaultImageByType } from 'shared/constants/defaultImages';
import LINKS from 'desktop/constants/navigation';
import s from './withTransportInfo.css';

const UAH_CURRENCY = currencies.find(item => item.slug === 'uah');
const USD_CURRENCY = currencies.find(item => item.slug === 'usd');
const EUR_CURRENCY = currencies.find(item => item.slug === 'eur');
const euroAgencyId = [336, 7261, 9828];

const withTransportInfo = WrappedComponent =>
  class extends React.Component {
    static displayName = `withTransportInfo(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      carInfo: PropTypes.shape().isRequired,
      carInfoFilter: PropTypes.arrayOf(PropTypes.string),
      //
      intl: intlShape.isRequired,
    };
    static defaultProps = {
      style: null,
      count: null,
      spares: [],
      carInfoFilter: [],
    };

    getCorrectTransport = transport => {
      if (!transport) {
        return null;
      } else if (LINKS.SEARCH[transport.slug]) {
        return transport;
      } else if (
        transport.parents &&
        transport.parents.length > 0 &&
        LINKS.SEARCH[transport.parents[0].slug]
      ) {
        return transport.parents[0];
      }

      return null;
    };

    generateEngineTitle = (engine, capacity, capacityUnit, gasEquipment, power, powerUnit) =>
      <span>
        {[
          engine && engine.title,
          capacity && capacityUnit && capacityUnit.title
            ? `${capacity} ${this.props.intl.formatMessage({ id: capacityUnit.title })}`
            : null,
          power && powerUnit && powerUnit.title
            ? `${power} ${powerUnit && this.props.intl.formatMessage({ id: powerUnit.title })}`
            : null,
        ]
          .filter(x => x)
          .join(' / ')}
        {gasEquipment && <div>ГБО</div>}
      </span>;

    buildInfoLink = (prefix, obj) => {
      if (!prefix || !obj) {
        return null;
      }

      const link = obj && obj.slug ? `${prefix}/${obj.slug}/`.replace(/\/\//g, '/') : null;

      return (
        link &&
        obj.title &&
        <Link className={cx(s.infoLink, { [s.notLink]: !link })} to={link || undefined}>
          {this.props.intl.formatMessage({ id: obj.title })}
        </Link>
      );
    };

    buildTransportInfoString = (link, correctTransport) => {
      const { intl, carInfo } = this.props;
      const { transport } = carInfo;

      const isAuto = transport && transport.slug === 'avto';
      const innerText = (correctTransport || transport || {}).title || null;

      return !isAuto && link && correctTransport
        ? <Link className={cx(s.infoLink, { [s.notLink]: !link })} to={link || undefined}>
            {correctTransport ? intl.formatMessage({ id: transport.title }) : null}
          </Link>
        : intl.formatMessage({ id: innerText });
    };

    render() {
      const { carInfo: _carInfo, intl } = this.props;

      const carInfo = { ..._carInfo };
      carInfo.author = carInfo.author || {};
      carInfo.make = carInfo.make || {};
      carInfo.model = carInfo.model || {};
      carInfo.modification = carInfo.modification || {};
      carInfo.location = carInfo.location || {};
      carInfo.characteristics = carInfo.characteristics || {};
      const {
        make,
        model,
        transport,
        price: prices,
        modification,
        location,
        year,
        mileage,
        mileage_unit: mileageUnit,
        color,
        ecotype,
        power,
        power_unit: powerUnitId,
        capacity_unit: capacityUnitId,
        capacity: oldAdvertCapacity,
        author,
        characteristics: { capacity: newAdvertCapacity },
        is_cashless: isCashless,
        is_not_cleared: isNotCleared,
        is_hot: isHot,
        is_new: isNew,
        is_rent: isRent,
        is_swap: isSwap,
        gas_equipment: gasEquipment,
        vin,
        equipment,
        capacity_body: capacityBody,
        axises,
        dimension_weight: dimensionWeight,
        admission_height: admissionHeight,
        arm_boom: armBoom,
        productivity,
        tonnage,
        dimension_width: dimensionWidth,
        dimension_height: dimensionHeight,
        dimension_length: dimensionLength,
        capacity_bucket: capacityBucket,
        lift_height: liftHeight,
        laying_width: layingWidth,
        dep_big: depBig,
        seats,
        overall_length: overallLength,
        dry_weight: dryWeight,
        draft,
        displacement,
        fuel_tank: fuelTank,
        driven_from: drivenFrom,
        abroad,
        water_tank: waterTank,
        seaworthiness_class: seaworthinessClass,
        category: { parents = [] },
        characteristics,
        body,
        currency,
        used_warranty: usedWarranty,
      } = carInfo;

      const engine = getFieldFromRootOrCharacteristics('engine', carInfo);
      const gearbox = getFieldFromRootOrCharacteristics('gearbox', carInfo);
      const drive = getFieldFromRootOrCharacteristics('drive', carInfo);
      const category = getFieldFromRootOrCharacteristics('category', carInfo);
      const brakes = getFieldFromRootOrCharacteristics('brakes', carInfo);
      const suspension = getFieldFromRootOrCharacteristics('suspension', carInfo);
      const unloading = getFieldFromRootOrCharacteristics('unloading', carInfo);
      const cabin = getFieldFromRootOrCharacteristics('cabin', carInfo);
      const chassisSuspension = getFieldFromRootOrCharacteristics('chassis_suspension', carInfo);
      const cabinSuspension = getFieldFromRootOrCharacteristics('cabin_suspension', carInfo);
      const engineOrder = getFieldFromRootOrCharacteristics('engine_order', carInfo);
      const countTacts = getFieldFromRootOrCharacteristics('tacts', carInfo);

      const capacity =
        newAdvertCapacity && newAdvertCapacity.title ? newAdvertCapacity.title : oldAdvertCapacity;

      const agency = carInfo.agency && carInfo.agency.id ? carInfo.agency : null;
      let secondCurrency = USD_CURRENCY;

      if (agency && euroAgencyId.includes(agency.id) && currency && currency.slug === 'eur')
        secondCurrency = EUR_CURRENCY;

      const priceList = [UAH_CURRENCY, secondCurrency] // todo duplicate
        .map(currencyItem => [
          prices.find(priceObj => priceObj.currency === currencyItem.slug),
          currencyItem,
        ])
        .filter(([priceObj]) => priceObj)
        .map(([{ value }, currencyItem]) => ({
          value: numberWithSpaces(value),
          currency: getTitleOrEmpty(currencyItem),
        }));

      const title = cleanArray([
        getTitleOrEmpty(make) || carInfo.make_title,
        getTitleOrEmpty(model) || carInfo.model_title,
        getTitleOrEmpty(modification),
        getTitleOrEmpty(equipment),
        year,
      ]).join(' ');
      const locTitle = getTitleOrEmpty(location);
      const translateLocTitle = locTitle ? intl.formatMessage({ id: locTitle }) : '';

      const seoTitle = `${intl.formatMessage({ id: 'Купить' })} ${title} ${translateLocTitle
        ? `в ${cityLocale(location.title, intl.locale, 'genitive')}`
        : ''}`;

      const alt = cleanArray([
        getTitleOrEmpty(make) || carInfo.make_title,
        getTitleOrEmpty(model) || carInfo.model_title,
        year,
        getTitleOrEmpty(color),
      ]).join(' ');

      const mileageUnitTitle = (mileageUnits.find(unit => unit.id === mileageUnit) || {}).title;
      const powerUnit = powerUnits.find(item => item.id === powerUnitId) || {};
      const capacityUnit = capacityUnits.find(item => item.id === capacityUnitId) || {};
      let mileageStatus =
        mileage > 0 ? `${mileage} ${intl.formatMessage({ id: mileageUnitTitle })}` : null;

      if (isNew) {
        mileageStatus = intl.formatMessage({ id: 'новый' });
      }

      const customStatus = isNotCleared
        ? intl.formatMessage({ id: 'нерастаможен' })
        : intl.formatMessage({ id: 'растаможен' });

      const correctTransport = this.getCorrectTransport(transport);
      const q = `/${generateSearchUrl({ isNew, transport: correctTransport, make, model })}`;

      const isAuto = transport && transport.slug === 'avto';
      const isUsedAuto = isAuto && !isNew;
      const isNewAuto = isAuto && isNew;

      const gearboxLink = isUsedAuto ? this.buildInfoLink(q, gearbox) : gearbox && gearbox.title;
      const engineLink = isUsedAuto
        ? this.buildInfoLink(q, engine, !isUsedAuto)
        : engine && engine.title;

      const categoryLink = !isNewAuto
        ? this.buildInfoLink(q, category)
        : category && category.title;

      const carSeriesLink = isAuto && this.buildInfoLink(q, characteristics.category);
      const carBodyLink = isAuto && this.buildInfoLink(q, body);

      const transportLink = this.buildTransportInfoString(q, correctTransport);

      const tempInfoList = [
        {
          key: 'location',
          title: intl.formatMessage({ id: 'Город' }),
          value: (location && location.title) || null,
          Icon: <LocationIcon />,
        },
        {
          key: 'year',
          title: intl.formatMessage({ id: 'Год выпуска' }),
          value: year || null,
          decorationClassName: 'b',
          Icon: <CalendarIcon />,
        },
        {
          key: 'mileage',
          title: intl.formatMessage({ id: 'Пробег' }),
          value: mileageStatus || null,
          decorationClassName: 'b',
          Icon: <SpeedIcon />,
        },
        {
          key: 'modification',
          title: intl.formatMessage({ id: 'Модификация' }),
          value: getTitleOrEmpty(modification),
          Icon: <CarIcon />,
        },
        {
          key: 'equipment',
          title: intl.formatMessage({ id: 'Комплектация' }),
          value: getTitleOrEmpty(equipment),
        },
        {
          key: 'body',
          title: intl.formatMessage({ id: 'Кузов' }),
          value:
            carSeriesLink ||
            carBodyLink ||
            ((transport && transport.title) !== (category && category.title) ? categoryLink : null),
          Icon: <CarIcon />,
        },
        // {
        //   title: intl.formatMessage({ id: 'Тип транспорта' }),
        //   value:
        //     (transport && transport.title) === (category && category.title)
        //       ? transportLink
        //       : (transport && intl.formatMessage({ id: transport.title })) || null,
        // },
        {
          key: 'parents',
          title: intl.formatMessage({ id: 'Тип' }),
          value:
            (parents && parents.length === 2 && intl.formatMessage({ id: parents[1].title })) ||
            null,
        },
        {
          key: 'color',
          title: intl.formatMessage({ id: 'Цвет' }),
          value: color && color.title ? intl.formatMessage({ id: color.title }) : null,
          Icon: <ColorIcon />,
        },
        {
          key: 'engine',
          title: intl.formatMessage({ id: 'двигатель' }),
          value:
            engine &&
            engine.title &&
            (engineLink
              ? <span>
                  {engineLink}
                  {' / '}
                  {this.generateEngineTitle(
                    null,
                    capacity,
                    capacityUnit,
                    gasEquipment,
                    power,
                    powerUnit,
                  )}
                </span>
              : this.generateEngineTitle(
                  engine,
                  capacity,
                  capacityUnit,
                  gasEquipment,
                  power,
                  powerUnit,
                )),
          Icon: <EngineIcon />,
        },
        {
          key: 'power',
          title: intl.formatMessage({ id: 'Мощность' }),
          value: power
            ? `${power} ${powerUnit && intl.formatMessage({ id: powerUnit.title })}`
            : null,
        },
        {
          key: 'gearbox',
          title: intl.formatMessage({ id: 'Коробка' }),
          value: gearboxLink || null,
          Icon: <GearboxIcon />,
        },
        {
          key: 'drive',
          title: intl.formatMessage({ id: 'Привод' }),
          value: drive && drive.title ? intl.formatMessage({ id: drive.title }) : null,
          Icon: <ChassisIcon />,
        },
        {
          key: 'vin',
          title: 'VIN',
          value: vin,
          Icon: <VinIcon />,
        },
        {
          key: 'customStatus',
          title: intl.formatMessage({ id: 'Таможня' }),
          value: customStatus || null,
        },
        {
          key: 'ecotype',
          title: intl.formatMessage({ id: 'Эконормы' }),
          value: getTitleOrEmpty(ecotype),
        },
        {
          key: 'isSwap',
          title: intl.formatMessage({ id: 'Обмен' }),
          value: isSwap ? intl.formatMessage({ id: 'Возможен' }) : null,
          decorationClassName: 'a',
        },
        {
          key: 'isHot',
          title: intl.formatMessage({ id: 'Срочность' }),
          value: isHot ? intl.formatMessage({ id: 'Горячее' }) : null,
          decorationClassName: 'a',
        },
        {
          key: 'isRent',
          title: intl.formatMessage({ id: 'Предложение' }),
          value: isRent ? intl.formatMessage({ id: 'Аренда' }) : null,
        },
        {
          key: 'isCashless',
          title: intl.formatMessage({ id: 'Безналичная оплата' }),
          value: isCashless ? intl.formatMessage({ id: 'Возможна' }) : null,
        },
        {
          key: 'capacity_body',
          title: intl.formatMessage({ id: 'Объем кузова' }),
          value: capacityBody ? `${capacityBody} м3` : null,
        },
        {
          key: 'axises',
          title: intl.formatMessage({ id: 'Кол-во осей' }),
          value: axises ? `${axises} шт.` : null,
        },
        {
          key: 'dimension_weight',
          title: intl.formatMessage({ id: 'Масса' }),
          value: dimensionWeight ? `${dimensionWeight} т.` : null,
        },
        {
          key: 'admission_height',
          title: intl.formatMessage({ id: 'Высота подачи' }),
          value: admissionHeight ? `${admissionHeight} м` : null,
        },
        {
          key: 'arm_boom',
          title: intl.formatMessage({ id: 'Вылет стрелы' }),
          value: armBoom ? `${armBoom} м` : null,
        },
        {
          key: 'productivity',
          title: intl.formatMessage({ id: 'Наработка' }),
          value: productivity ? `${productivity} м\\ч` : null,
        },
        {
          key: 'tonnage',
          title: intl.formatMessage({ id: 'Грузоподъемность' }),
          value: tonnage ? `${tonnage} т.` : null,
        },
        {
          key: 'dimension_width',
          title: intl.formatMessage({ id: 'Ширина' }),
          value: dimensionWidth ? `${dimensionWidth} м` : null,
        },
        {
          key: 'dimension_height',
          title: intl.formatMessage({ id: 'Высота' }),
          value: dimensionHeight ? `${dimensionHeight} м` : null,
        },
        {
          key: 'dimension_length',
          title: intl.formatMessage({ id: 'Длина' }),
          value: dimensionLength ? `${dimensionLength} м` : null,
        },
        {
          key: 'capacity_bucket',
          title: intl.formatMessage({ id: 'Объем ковша' }),
          value: capacityBucket ? `${capacityBucket} м3` : null,
        },
        {
          key: 'lift_height',
          title: intl.formatMessage({ id: 'Высота подъема' }),
          value: liftHeight ? `${liftHeight} м` : null,
        },
        {
          key: 'laying_width',
          title: intl.formatMessage({ id: 'Ширина укладки' }),
          value: layingWidth ? `${layingWidth} м` : null,
        },
        {
          key: 'dep_big',
          title: intl.formatMessage({ id: 'Глубина копания' }),
          value: depBig ? `${depBig} м` : null,
        },
        {
          key: 'brakes',
          title: intl.formatMessage({ id: 'Тип тормозов' }),
          value: brakes && brakes.title ? brakes.title : null,
        },
        {
          key: 'suspension',
          title: intl.formatMessage({ id: 'Тип подвески' }),
          value: suspension && suspension.title ? suspension.title : null,
        },
        {
          key: 'unloading',
          title: intl.formatMessage({ id: 'Выгрузка' }),
          value: unloading && unloading.title ? unloading.title : null,
        },
        {
          key: 'cabin',
          title: intl.formatMessage({ id: 'Кабина' }),
          value: cabin && cabin.title ? cabin.title : null,
        },
        {
          key: 'chassis_suspension',
          title: intl.formatMessage({ id: 'Подвеска шасси' }),
          value: chassisSuspension && chassisSuspension.title ? chassisSuspension.title : null,
        },
        {
          key: 'cabin_suspension',
          title: intl.formatMessage({ id: 'Подвеска кабины' }),
          value: cabinSuspension && cabinSuspension.title ? cabinSuspension.title : null,
        },
        {
          key: 'seats',
          title: intl.formatMessage({ id: 'Количество мест' }),
          value: seats || null,
        },
        {
          key: 'engine_order',
          title: intl.formatMessage({ id: 'Расположение цилиндров' }),
          value: engineOrder && engineOrder.title ? engineOrder.title : null,
        },
        {
          key: 'count_tacts',
          title: intl.formatMessage({ id: 'Число тактов' }),
          value: countTacts && countTacts.title ? countTacts.title : null,
        },
        {
          key: 'overall_length',
          title: intl.formatMessage({ id: 'Габаритная длина'.toLowerCase() }),
          value: overallLength ? `${overallLength} м` : null,
        },
        {
          key: 'dry_weight',
          title: intl.formatMessage({ id: 'Сухой вес'.toLowerCase() }),
          value: dryWeight ? `${dryWeight} т` : null,
        },
        {
          key: 'draft',
          title: intl.formatMessage({ id: 'Осадка'.toLowerCase() }),
          value: draft ? `${draft} м` : null,
        },
        {
          key: 'displacement',
          title: intl.formatMessage({ id: 'Водоизмещение'.toLowerCase() }),
          value: displacement ? `${displacement} т` : null,
        },
        {
          key: 'fuel_tank',
          title: intl.formatMessage({ id: 'Топливный бак'.toLowerCase() }),
          value: fuelTank ? `${fuelTank} л` : null,
        },
        {
          key: 'water_tank',
          title: intl.formatMessage({ id: 'Бак для воды'.toLowerCase() }),
          value: waterTank ? `${waterTank} л` : null,
        },
        {
          key: 'seaworthiness_class',
          title: intl.formatMessage({ id: 'СЕ категория (класс мореходности)'.toLowerCase() }),
          value: seaworthinessClass ? seaworthinessClass.toUpperCase() : null,
        },
        {
          key: 'driven_from',
          title: intl.formatMessage({ id: 'Пригнан' }),
          value:
            drivenFrom && drivenFrom.id ? getCountryById(drivenFrom.id).from[intl.locale] : null,
        },
        {
          key: 'abroad',
          title: intl.formatMessage({ id: 'Cостояние' }),
          value: abroad ? intl.formatMessage({ id: 'авто не в Украине' }) : null,
        },
      ].filter(item => item.value);

      const { carInfoFilter } = this.props;
      const infoList =
        Array.isArray(carInfoFilter) && carInfoFilter.length
          ? tempInfoList.filter(item => item.key && carInfoFilter.includes(item.key))
          : tempInfoList;

      // Seller info
      const authorName = agency && agency.title ? agency.title : getUserName(author);
      const sellerLocation = (location && location.title) || (agency && agency.address);

      const sellerLink = agency && agency.slug ? getDealerLink(agency) : null;

      const sellerExtraText =
        agency && agency.adverts_count
          ? `${agency.adverts_count} ${intl.formatMessage({ id: 'объявлений' })} `
          : null;

      const warranty = usedWarranty && agency && agency.warranty_active && agency.warranty;

      const defaultImage = getDefaultImageByType(transport);
      const transformedData = {
        title,
        seoTitle,
        alt,
        agency,
        defaultImage,
        priceList,
        infoList,
        authorName,
        sellerExtraText,
        sellerLink,
        sellerLocation,
        warranty,
      };
      return (
        <div>
          <WrappedComponent {...this.props} transformedData={transformedData} />
        </div>
      );
    }
  };

export default component => withStyles(s)(injectIntl(withTransportInfo(component)));



// WEBPACK FOOTER //
// ./src/shared/hocs/withTransportInfo/withTransportInfo.js